import {
  useGetEnrollmentJointConfirmInformationData,
  useQueryClient,
  useSubmitEnrollmentJointConfirmInformationData,
  useSubmitEnrollmentJointConfirmInformationSwitchData,
} from '@egw/api'
import { Alert, AlertType, Button, GenericError, ProgressBar, Tag, useModalState } from '@egw/components'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { riskService } from '@egw/risk-service'
import { formatInputToCurrency, formatPhoneNumber } from '@egw/utils'
import { UserIcon } from '@heroicons/react/24/solid'
import { type FC, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { setContext } from '@egw/events'
import { PageHeader } from '../../../components'
import { ConfirmationSection } from '../../../components/confirmation-section/confirmation-section'
import {
  EditFirstNameModal,
  EditLastNameModal,
  EditEmploymentModal,
} from '../../../components/confirm-information-modals'
import { EnrollmentPageLoading } from '../../../components/page-loading'
import { ROUTES as GENERIC_ROUTES } from '../../../constants/routes/routes-generic'
import { ROUTES as JOINT_ROUTES } from '../../../constants/routes/routes-joint'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { useHandleEnrollmentErrors } from '../../../utils/joint/api-errors/enrollment-error-handler'
import { enrollmentCookieService } from '@egw/enrollment-cookie'

const log = new Logger('Enrollment - Joint - ConfirmInformation')

const getBackPath = (route: string): string => {
  return `${WEALTH_ROUTES.ENROLLMENT.path}/${route}?edit=true`
}

export const ConfirmInformationPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { flags } = useFlags<FeatureFlags>()

  const { data: apiData, isLoading, error } = useGetEnrollmentJointConfirmInformationData()

  // Local state to hold updated data from modals
  const [localData, setLocalData] = useState<typeof apiData>(null)

  // Use local data if available, otherwise use API data
  const data = localData || apiData

  // Generic helper function to update any field in activePerson
  const updateActivePersonField = useCallback(
    (
      updater: (activePerson: NonNullable<typeof data>['activePerson']) => NonNullable<typeof data>['activePerson'],
      fieldName: string,
    ) => {
      if (data?.activePerson) {
        setLocalData({
          ...data,
          activePerson: updater(data.activePerson),
        })
        log.info(`${fieldName} updated successfully`)
      }
    },
    [data],
  )
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointConfirmInformationData()
  const {
    mutate: mutateSwitch,
    isLoading: isSubmittingSwitch,
    error: _submitErrorSwitch,
  } = useSubmitEnrollmentJointConfirmInformationSwitchData()

  // Modal state for Edit First Name
  const {
    isOpen: isEditFirstNameModalOpen,
    openModal: openEditFirstNameModal,
    closeModal: closeEditFirstNameModal,
  } = useModalState()

  // Modal state for Edit Last Name
  const {
    isOpen: isEditLastNameModalOpen,
    openModal: openEditLastNameModal,
    closeModal: closeEditLastNameModal,
  } = useModalState()

  // Modal state for Edit Employment (includes both status and occupation)
  const {
    isOpen: isEditEmploymentModalOpen,
    openModal: openEditEmploymentModal,
    closeModal: closeEditEmploymentModal,
  } = useModalState()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint confirm information data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint confirm information form', submitError)
    }
  }, [submitError])

  const progress = data?.activePerson?.progress
  const profileName = data?.activePerson?.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''

  const sections = [
    {
      title: 'Personal Info',
      fields: [
        {
          value: 'Legal first name',
          rightSide: data?.activePerson?.name?.firstName,
          testId: 'firstName',
          onEdit: () => {
            if (flags.rolloutJointEnrollmentConfirmInformationV2) {
              openEditFirstNameModal()
            } else {
              void navigate(getBackPath(JOINT_ROUTES.CONTACT_INFO.path))
            }
          },
          isPII: true,
        },
        {
          value: 'Legal last name',
          rightSide: data?.activePerson?.name?.lastName,
          testId: 'lastName',
          onEdit: () => {
            if (flags.rolloutJointEnrollmentConfirmInformationV2) {
              openEditLastNameModal()
            } else {
              void navigate(getBackPath(JOINT_ROUTES.CONTACT_INFO.path))
            }
          },
          isPII: true,
        },
        {
          value: 'Phone',
          rightSide: formatPhoneNumber(data?.activePerson?.phone?.phoneNumber),
          testId: 'phoneNumber',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.CONTACT_INFO.path))
          },
          isPII: true,
        },
        {
          value: 'Email',
          rightSide: data?.activePerson?.email,
          testId: 'email',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.CONTACT_INFO.path))
          },
          isPII: true,
        },
        {
          value: 'Address',
          rightSide: (
            <p className="flex flex-col justify-end items-end">
              <span>
                {data?.activePerson?.addressLine1}
                {data?.activePerson?.addressLine2 ? `, ${data.activePerson.addressLine2}` : ''}
              </span>
              <span>
                {data?.activePerson?.addressCity}, {data?.activePerson?.addressRegionCode}{' '}
                {data?.activePerson?.addressPostalCode}
              </span>
            </p>
          ),
          testId: 'address',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.HOME_ADDRESS.path))
          },
          isPII: true,
        },
        {
          value: 'DOB',
          rightSide: data?.activePerson?.dateOfBirth,
          testId: 'dateOfBirth',
          onEdit: () => {
            void navigate(getBackPath('joint/verify-identity'))
          },
          isPII: true,
        },
        {
          value: 'SSN/ITIN',
          rightSide: data?.activePerson?.maskedTaxId,
          testId: 'ssn',
          onEdit: () => {
            void navigate(getBackPath('joint/verify-identity'))
          },
          isPII: true,
        },
      ],
    },
    {
      title: 'Employment',
      fields: [
        {
          value: 'Employment status',
          rightSide: t(`joint.confirm_information.employment_status.${data?.activePerson?.employmentStatus ?? ''}`),
          testId: 'employmentStatus',
          onEdit: () => {
            if (flags.rolloutJointEnrollmentConfirmInformationV2) {
              openEditEmploymentModal()
            } else {
              void navigate(getBackPath(JOINT_ROUTES.EMPLOYMENT.path))
            }
          },
        },
        ...(data?.activePerson?.occupation
          ? [
              {
                value: 'Occupation',
                rightSide: t(`joint.confirm_information.occupation.${data.activePerson.occupation}`),
                testId: 'occupation',
                onEdit: () => {
                  if (flags.rolloutJointEnrollmentConfirmInformationV2) {
                    openEditEmploymentModal()
                  } else {
                    void navigate(getBackPath(JOINT_ROUTES.EMPLOYMENT.path))
                  }
                },
              },
            ]
          : []),
        {
          value: 'Brokerage affiliation',
          rightSide: data?.activePerson?.isBrokerDealerEmployer ? 'Yes' : 'No',
          testId: 'brokerageAffiliation',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.SUITABILITY.path))
          },
        },
        {
          value: 'Executive or shareholder',
          rightSide: data?.activePerson?.isDirectorPolicyMaker ? 'Yes' : 'No',
          testId: 'isDirectorPolicyMakerOfCompany',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.SUITABILITY.path))
          },
        },
        {
          value: 'Politically exposed person (PEP)',
          rightSide: data?.activePerson?.isPoliticallyExposedPerson ? 'Yes' : 'No',
          testId: 'pep',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.SUITABILITY.path))
          },
        },
        {
          value: 'Immediate family member or close associate of politically exposed person (PEP)',
          rightSide: data?.activePerson?.hasPoliticallyExposedImmediateFamilyMembers ? 'Yes' : 'No',
          testId: 'family_member_pep',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.SUITABILITY.path))
          },
          isPII: true,
        },
      ],
    },
    {
      title: 'Finances',
      fields: [
        {
          value: 'Marital status',
          rightSide: t(`joint.confirm_information.marital_status.${data?.activePerson?.maritalStatus ?? ''}`),
          testId: 'maritalStatus',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.FINANCES.path))
          },
        },
        {
          value: 'Tax filing status',
          rightSide: t(
            `joint.confirm_information.filing-status.tax_filing_status.${data?.activePerson?.taxFilingStatus ?? ''}`,
          ),
          testId: 'filingStatus',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.FINANCES.path))
          },
        },
        {
          value: 'Annual income',
          rightSide: formatInputToCurrency(data?.activePerson?.personalAnnualGrossIncome?.toString() ?? '', true),
          testId: 'annualIncome',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.FINANCES.path))
          },
          isPII: true,
        },
        {
          value: 'Net worth',
          rightSide: t(
            `joint.confirm_information.personal_net_worth_range.${data?.activePerson?.personalNetWorthRange ?? ''}`,
          ),
          testId: 'netWorth',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.FINANCES.path))
          },
          isPII: true,
        },
        {
          value: 'Investment objective',
          rightSide: t(`joint.investment-goals.investment_objective.${data?.activePerson?.investmentObjective ?? ''}`),
          testId: 'investmentObjective',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.INVESTMENT_GOALS.path))
          },
        },
        {
          value: 'Investment experience',
          rightSide: t(
            `joint.investment-goals.investment_experience.${data?.activePerson?.investmentExperience ?? ''}`,
          ),
          testId: 'investmentExperience',
          onEdit: () => {
            void navigate(getBackPath(JOINT_ROUTES.INVESTMENT_GOALS.path))
          },
        },
      ],
    },
  ]

  // Protect route
  useValidateRoute(data?.activePerson?.path)

  // Error handler for enrollment errors
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Joint - ConfirmInformation')

  const onSubmit = async (): Promise<void> => {
    mutate({
      activePerson: {
        proceedConfirmInfo: true,
      },
    })
      .then((response) => {
        // Check for API errors in the response
        if (response && handleEnrollmentErrors(response)) {
          return
        }

        if (!response?.isError) {
          // Handle switch session
          void mutateSwitch({
            activePerson: {
              triggerRiskCheck: true,
              switchSession: true,
            },
          }).then((responseSwitch) => {
            // Check for API errors in the switch response
            if (responseSwitch && handleEnrollmentErrors(responseSwitch)) {
              return
            }

            // Clear the query client here, to have fresh data on authentication
            queryClient.clear()
            if (!responseSwitch?.isError) {
              // Reset Risk Session Cookie value
              void riskService.resetSession()

              void mutate({}).then((response3) => {
                // Check for API errors in the final response
                if (response3 && handleEnrollmentErrors(response3)) {
                  return
                }

                if (!response3?.isError) {
                  // Set userId for PERSON 2 through context update
                  setContext({ userId: response3?.activePerson?.applicationId })
                  void navigate(response3?.activePerson?.path ?? '')
                } else {
                  log.error('No path returned from final mutation, redirecting to error page')
                  void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${GENERIC_ROUTES.ERROR.path}`)
                }
              })
            } else {
              log.error('Switch session mutation failed, redirecting to error page')
              void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${GENERIC_ROUTES.ERROR.path}`)
            }
          })
        } else if (!response.activePerson?.path) {
          log.error('No path returned from initial mutation, redirecting to error page')
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${GENERIC_ROUTES.ERROR.path}`)
        }
      })
      .catch((e: unknown) => {
        log.error('Error submitting confirm information form', e)
      })
  }

  return (
    <EnrollmentJointLayout title={t('joint.confirm_information.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? <EnrollmentPageLoading testId="confirm_information-loading" /> : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {/* AUTO GENERATED COMPONENTS START*/}
          <div className="w-full flex-1 flex flex-col justify-start">
            {profileName ? (
              <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
                {profileName}
              </Tag>
            ) : null}
            <PageHeader
              title={t('joint.confirm_information.title')}
              // backPath={`${WEALTH_ROUTES.ENROLLMENT.path}/bd-shareholder`}
            />
            <ProgressBar showAnimation progressPercentage={progress} className="mb-md" />
            <p className="typo-primary-base-regular text-text-meta mb-xl">
              {t('joint.confirm_information.description')}
            </p>
            {sections.map((e) => {
              return (
                <ConfirmationSection
                  title={e.title}
                  fields={e.fields}
                  key={e.title.toLowerCase().replaceAll(' ', '-')}
                />
              )
            })}
            <div className="flex-1 md:hidden" />

            {/* AUTO GENERATED COMPONENTS */}

            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}

            <Button
              type="button"
              className="w-full md:w-min mt-xl"
              isLoading={isSubmitting || isSubmittingSwitch}
              testId="primaryAction_button"
              onClick={() => void onSubmit()}
            >
              {t('joint.confirm_information.continue')}
            </Button>
          </div>
          {/* AUTO GENERATED COMPONENTS END*/}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}

      {/* Edit Modals - Only render when V2 flag is enabled */}
      {flags.rolloutJointEnrollmentConfirmInformationV2 ? (
        <>
          <EditFirstNameModal
            isOpen={isEditFirstNameModalOpen}
            onClose={closeEditFirstNameModal}
            currentValue={data?.activePerson?.name?.firstName}
            currentLastName={data?.activePerson?.name?.lastName}
            onSuccess={(updatedFirstName?: string) => {
              if (updatedFirstName && data?.activePerson?.name) {
                updateActivePersonField((activePerson) => {
                  if (!activePerson) return activePerson
                  return {
                    ...activePerson,
                    name: {
                      firstName: updatedFirstName,
                      lastName: activePerson.name!.lastName,
                    },
                  }
                }, 'firstName')
              }
            }}
          />
          <EditLastNameModal
            isOpen={isEditLastNameModalOpen}
            onClose={closeEditLastNameModal}
            currentValue={data?.activePerson?.name?.lastName}
            currentFirstName={data?.activePerson?.name?.firstName}
            onSuccess={(updatedLastName?: string) => {
              if (updatedLastName && data?.activePerson?.name) {
                updateActivePersonField((activePerson) => {
                  if (!activePerson) return activePerson
                  return {
                    ...activePerson,
                    name: {
                      firstName: activePerson.name!.firstName,
                      lastName: updatedLastName,
                    },
                  }
                }, 'lastName')
              }
            }}
          />
          <EditEmploymentModal
            isOpen={isEditEmploymentModalOpen}
            onClose={closeEditEmploymentModal}
            currentEmploymentStatus={data?.activePerson?.employmentStatus}
            currentOccupation={data?.activePerson?.occupation}
            onSuccess={(updatedEmploymentStatus?: string, updatedOccupation?: string) => {
              if (updatedEmploymentStatus !== undefined) {
                updateActivePersonField((activePerson) => {
                  if (!activePerson) return activePerson
                  return {
                    ...activePerson,
                    employmentStatus: updatedEmploymentStatus,
                    occupation: updatedOccupation,
                  }
                }, 'employment')
              }
            }}
          />
        </>
      ) : null}
    </EnrollmentJointLayout>
  )
}
